import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type { FieldErrors } from '../reducers/dealScreenErrorReducer';

/** Namespace for the save flow's per-product errors inside the PROTECTION entity. */
export const VPP_PRODUCT_ERRORS_KEY = 'products';

/**
 * Sibling namespace for the contract flow's GAP errors, so each writer replaces only its own map.
 */
export const VPP_GAP_CONTRACT_ERRORS_KEY = 'gapContract';
export const VPP_CONTRACT_API_ERRORS_KEY = 'contractApi';

const MANUAL_KEY_PREFIX = 'manual';
const CATALOG_KEY_PREFIX = 'catalog';

/** Fields that identify a manual row. */
const MANUAL_IDENTITY_TEXT_FIELDS = ['productName', 'productCategoryCode', 'productCode', 'profitLocation'] as const;

/** Normalised, so a missing amount and 0 give the same identity. */
const MANUAL_IDENTITY_NUMERIC_FIELDS = ['productCost', 'productPrice'] as const;

/**
 * Row identity for a VPP product error, derived from the product rather than its position so it survives
 * sorting and pagination. Catalog products key on `productCode`; manual products key on their whole
 * editable content, because the manual Code field is dealer-typed and may repeat.
 */
export const getVppProductErrorKey = (product?: DraftDealProduct): string | undefined => {
    if (!product) return undefined;

    if (!product.isManualProduct) {
        return product.productCode ? `${CATALOG_KEY_PREFIX}:${product.productCode}` : undefined;
    }

    const identity = [
        ...MANUAL_IDENTITY_TEXT_FIELDS.map((field) => String(product[field] ?? '').trim()),
        ...MANUAL_IDENTITY_NUMERIC_FIELDS.map((field) => Number(product[field] ?? 0))
    ];

    return `${MANUAL_KEY_PREFIX}:${JSON.stringify(identity)}`;
};

/**
 * Re-keys the engine's index-based collection errors onto product identity. An entry that cannot be
 * uniquely addressed keeps a positional key that no row builds, so it counts toward the tab total
 * without lighting a row.
 */
export const buildVppProductErrors = (
    errorsByIndex: Record<string, FieldErrors> | null | undefined,
    products: DraftDealProduct[]
): Record<string, FieldErrors> => {
    if (!errorsByIndex) return {};

    const keyOccurrences = products.reduce((occurrences, product) => {
        const key = getVppProductErrorKey(product);
        if (key) occurrences.set(key, (occurrences.get(key) ?? 0) + 1);
        return occurrences;
    }, new Map<string, number>());

    return Object.entries(errorsByIndex).reduce((productErrors, [index, fieldErrors]) => {
        const key = getVppProductErrorKey(products[Number(index)]);
        const isAddressable = Boolean(key) && keyOccurrences.get(key as string) === 1;
        productErrors[isAddressable ? (key as string) : `index:${index}`] = fieldErrors;
        return productErrors;
    }, {} as Record<string, FieldErrors>);
};

/** Same re-keying for the GAP contract collection. */
export const buildGapContractErrors = (
    errorsByIndex: Record<string, FieldErrors> | null | undefined,
    gapProducts: Array<{ productCode?: string; isManualProduct?: boolean }>
): Record<string, FieldErrors> => buildVppProductErrors(errorsByIndex, gapProducts as DraftDealProduct[]);

/** Same re-keying for the contract-API coverage-term (Term Miles / Term Months) collection. */
export const buildContractApiErroredVppErrors = (
    errorsByIndex: Record<string, FieldErrors> | null | undefined,
    protectionProducts: Array<{ productCode?: string; isManualProduct?: boolean }>
): Record<string, FieldErrors> => buildVppProductErrors(errorsByIndex, protectionProducts as DraftDealProduct[]);
